/* =====================================================================
   Real-time "Most searched" populator
   ---------------------------------------------------------------------
   Replaces both the hardcoded keyword list AND the hardcoded product
   cards in the empty-state predictive-search panel with Shopify's real
   top searches. Two-stage pipeline:
     STAGE 1 — Top queries (popularity-ranked):
       Hit /search/suggest.json with seed letters; the `queries` array
       Shopify returns IS sorted by popularity. We dedupe across seeds
       to get the global top-N queries.
     STAGE 2 — Top products (from top queries):
       For each top query, hit /search/suggest.json again with that
       query as the search term — the top product for "cxo" really is
       the CXO chair, because the search ranks products by relevance to
       the query (popularity-influenced). Dedupe across queries and
       take the top-N products.
   This is correct because the `products` field returned for a LETTER
   seed is just "products whose name starts with that letter" (alpha),
   NOT popularity-ranked. To get the actual most-searched products we
   have to start from the top queries and resolve their products.

   This file is loaded by layout/theme.liquid (deferred), so the
   populator runs once per page-load regardless of whether the
   predictive-search snippet is rendered inline or lazy.
   ===================================================================== */
(function () {
  if (window.__naiTopQueriesWired) return;
  window.__naiTopQueriesWired = true;

  var DEBUG = true;
  function log() {
    if (!DEBUG) return;
    try { console.log.apply(console, ['[nai-top-searches]'].concat([].slice.call(arguments))); } catch (_) {}
  }
  // Always announce we loaded — single line, easy to grep in console.
  try { console.log('[nai-top-searches] script loaded'); } catch (_) {}
  // Immediate one-shot DOM probe so we can tell at a glance whether the
  // empty-state targets even exist on this page-load.
  try {
    var initialProbe = {
      queryLists: document.querySelectorAll('[data-nai-top-queries-list]').length,
      productGrid: !!document.querySelector('[data-nai-top-products-grid]')
    };
    console.log('[nai-top-searches] initial DOM probe', initialProbe);
  } catch (_) {}
  // Bust any stale cache so failed empty results don't shortcut us.
  try { sessionStorage.removeItem('naiTopSearches'); } catch (_) {}

  // Seeds are ordered by likely popularity for THIS store. Shopify's
  // suggest.json only ranks queries within a prefix, never globally —
  // so when we round-robin across seeds, the order we visit each seed
  // becomes the de-facto popularity order of the top results.
  //
  // The ordering below is calibrated to the live top-search data from
  // Search & Discovery Insights:
  //   c (cxo 56) > s (stool 26) > v (vxo 21) > i (ic2 19) >
  //   t (tonik 18) > b (bless 17) > r (raven 16) > n (nlc 14, nxo 14) >
  //   j (johnny 12) > w (wxo 12) > f (frio 12) > p (presider 11) >
  //   o (overtime 9, oxo 8) > m (malibu 6) > e (emily 7) > l (lxo 9) >
  //   d (danforth 9) > a (amalfi 5) > rare letters last
  //
  // If the underlying top-searches shift dramatically, re-order this
  // list to match — the rest of the pipeline doesn't need to change.
  var SEEDS = [
    'c','s','v','i','t','b','r','n','j','w','f','p','o','m','e','l','d','a','u','q',
    'k','h','g','x','y','z',
    '6','1','7','8','9','0','2','3','4','5'
  ];
  var PER_SEED            = 3;
  var FINAL_QUERY_LIMIT   = 12;
  var FINAL_PRODUCT_LIMIT = 4;
  var CACHE_KEY           = 'naiTopSearches';
  var CACHE_TTL_MS        = 15 * 60 * 1000; // 15 minutes

  function readCache() {
    try {
      var raw = sessionStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || (Date.now() - parsed.ts) > CACHE_TTL_MS) return null;
      return parsed.data;
    } catch (_) { return null; }
  }
  function writeCache(data) {
    try {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), data: data }));
    } catch (_) {}
  }

  // STAGE 1 — Get the top QUERIES (popularity-ranked) seeded by a letter.
  // We ignore the products in this response because for a letter seed
  // they're alpha-matched, not popularity-ranked.
  function fetchQueriesForSeed(letter) {
    var url =
      '/search/suggest.json?q=' + encodeURIComponent(letter) +
      '&resources[type]=query&resources[limit]=' + PER_SEED;
    return fetch(url, { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (d) {
        var results = (d && d.resources && d.resources.results) || {};
        return (results.queries || [])
          .map(function (q) { return (q.text || '').trim(); })
          .filter(Boolean);
      })
      .catch(function () { return []; });
  }

  // STAGE 2 — Resolve a top query into the product(s) users land on
  // when they search for it. The first product returned for "cxo" is
  // the CXO chair (highest relevance + popularity for that query).
  function fetchProductsForQuery(query) {
    var url =
      '/search/suggest.json?q=' + encodeURIComponent(query) +
      '&resources[type]=product&resources[limit]=2';
    return fetch(url, { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (d) {
        var results = (d && d.resources && d.resources.results) || {};
        return (results.products || []).map(function (p) {
          return {
            handle:       (p.handle       || '').trim(),
            title:         p.title        || '',
            url:           p.url          || ('/products/' + (p.handle || '')),
            image:         p.image        || (p.featured_image && p.featured_image.url) || '',
            vendor:        p.vendor       || '',
            productType:   p.product_type || '',
            price:         p.price        || '',
            sourceQuery:   query
          };
        }).filter(function (p) { return p.handle; });
      })
      .catch(function () { return []; });
  }

  // STAGE 3 helper — hit /products/{handle}.js to get the full product
  // record including `type`. Folds the result back onto the existing
  // product object (preserves sourceQuery, etc.).
  function enrichProduct(product) {
    if (!product.handle) return Promise.resolve(product);
    return fetch('/products/' + encodeURIComponent(product.handle) + '.js', {
      headers: { 'Accept': 'application/json' }
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (full) {
        if (!full) return product;
        return {
          handle:       product.handle,
          title:         full.title         || product.title,
          url:           full.url           || product.url,
          image:         (full.featured_image || full.images && full.images[0]) || product.image,
          vendor:        full.vendor        || product.vendor,
          productType:   full.type          || product.productType,
          price:         full.price         || product.price,
          sourceQuery:   product.sourceQuery
        };
      })
      .catch(function () { return product; });
  }

  function getTop() {
    var cached = readCache();
    if (cached && (cached.queries.length || cached.products.length)) {
      log('using cached top-searches', cached);
      return Promise.resolve(cached);
    }
    log('STAGE 1: fetching top queries via letter seeds…');
    return Promise.all(SEEDS.map(fetchQueriesForSeed)).then(function (lists) {
      // Round-robin merge across seeds so each letter contributes its #1
      // query before any letter contributes its #2. That makes
      //   c → cxo, s → stool, v → vxo, i → ic2, t → tonik, b → bless …
      // bubble to the front instead of c gobbling 4 spots with cxo +
      // cxo 6200 + cxo chairs 6200 + cricket.
      var qSeen = Object.create(null);
      var queries = [];
      var maxRound = 0;
      for (var i = 0; i < lists.length; i++) {
        if (lists[i].length > maxRound) maxRound = lists[i].length;
      }
      for (var round = 0; round < maxRound && queries.length < FINAL_QUERY_LIMIT; round++) {
        for (var s = 0; s < lists.length && queries.length < FINAL_QUERY_LIMIT; s++) {
          var q = lists[s][round];
          if (!q) continue;
          var qKey = q.toLowerCase();
          if (qSeen[qKey]) continue;
          qSeen[qKey] = true;
          queries.push(q);
        }
      }
      log('STAGE 1 result — top queries:', queries);
      if (!queries.length) return { queries: [], products: [] };

      // STAGE 2 — Resolve top queries to products in parallel.
      log('STAGE 2: resolving top queries to products…');
      return Promise.all(queries.map(fetchProductsForQuery)).then(function (resolved) {
        var pSeen = Object.create(null);
        var products = [];
        // Walk each query's resolved products in order. The first product
        // for "cxo" is the CXO chair, for "stool" it's the top Stool, etc.
        for (var k = 0; k < resolved.length; k++) {
          var pList = resolved[k];
          for (var m = 0; m < pList.length; m++) {
            var p = pList[m];
            if (pSeen[p.handle] || products.length >= FINAL_PRODUCT_LIMIT) continue;
            pSeen[p.handle] = true;
            products.push(p);
          }
        }
        log('STAGE 2 result — top products:', products.map(function (p) {
          return p.handle + ' (from "' + p.sourceQuery + '")';
        }));

        // STAGE 3 — Enrich each product with /products/{handle}.js data so
        // we get the authoritative product_type, full title, etc.
        // Shopify's /search/suggest.json endpoint frequently returns
        // empty product_type even when the product has a type set in
        // admin, but /products/{handle}.js always returns the canonical
        // record. Run in parallel — 4 small JSON requests is fine.
        log('STAGE 3: enriching products via /products/{handle}.js…');
        return Promise.all(products.map(enrichProduct)).then(function (enriched) {
          log('STAGE 3 result — enriched product_types:', enriched.map(function (p) {
            return p.handle + ' → "' + (p.productType || '∅') + '"';
          }));
          var result = { queries: queries, products: enriched };
          if (queries.length || enriched.length) writeCache(result);
          return result;
        });
      });
    });
  }

  function renderQueryList(listEl, queries) {
    if (!listEl || !queries.length) return;
    var html = '';
    for (var i = 0; i < queries.length; i++) {
      var q = queries[i];
      var href = '/search?type=product&options%5Bprefix%5D=last&q=' + encodeURIComponent(q);
      html +=
        '<li><a href="' + href +
        '" class="reversed-link text-capitalize">' +
        q.replace(/[<>&"']/g, function (c) {
          return ({ '<':'&lt;','>':'&gt;','&':'&amp;','"':'&quot;',"'":'&#39;' })[c];
        }) +
        '</a></li>';
    }
    listEl.innerHTML = html;
    log('wrote', queries.length, 'queries into list');
  }

  /* Build cards client-side from the JSON we already have from
     /search/suggest.json. Section-rendering against main-search returns
     the section's *initial* server-rendered shell (no cards — the theme
     uses facets.js/search.js to lazy-render results client-side), so it
     can't be used to lift card markup. Instead we clone the existing
     curated card already in the DOM (which gives us the theme's exact
     .card-product structure + classes) and swap in the new data. If no
     curated card exists, fall back to a minimal hand-rolled card. */

  function swapTextNodeOrFirstTextChild(el, value) {
    if (!el || value == null) return;
    // Replace the first non-empty text node we find; otherwise set textContent.
    for (var i = 0; i < el.childNodes.length; i++) {
      var n = el.childNodes[i];
      if (n.nodeType === 3 && n.nodeValue.trim()) {
        n.nodeValue = value;
        return;
      }
    }
    el.textContent = value;
  }

  function rewriteCardLinks(card, url) {
    if (!url) return;
    var links = card.querySelectorAll('a[href]');
    links.forEach(function (a) {
      // Skip non-product links (favorites, quick view, etc.) — they
      // typically have data attributes or special classes; product links
      // typically point to /products/.
      var href = a.getAttribute('href') || '';
      if (href.indexOf('/products/') === 0 || href === '#' || href.indexOf('?') === 0) {
        a.setAttribute('href', url);
      }
    });
  }

  function rewriteCardImage(card, imageUrl) {
    if (!imageUrl) return;
    // Build a few common Shopify image variants to make srcset replacement
    // simpler — but for our purposes, blanket-setting src + srcset to the
    // base URL is enough; the browser will display it at whichever size.
    var imgs = card.querySelectorAll('img');
    imgs.forEach(function (img) {
      img.setAttribute('src', imageUrl);
      img.removeAttribute('srcset');
      img.removeAttribute('data-src');
      img.removeAttribute('data-srcset');
      // Some themes have a loading="lazy" placeholder swap — clear that.
      if (img.classList) img.classList.remove('lazyload', 'lazyloading');
      img.classList && img.classList.add('lazyloaded');
    });
    // Some card-product variants have a <noscript><img></noscript> too; drop it.
    var ns = card.querySelectorAll('noscript');
    ns.forEach(function (n) { n.remove(); });
  }

  // Read the server-rendered price map. Cached on first call so we
  // only parse the JSON once per page. Returns an object keyed by
  // product handle: { handle: { usd: number, cad: number } }
  var __priceMap = null;
  function getPriceMap() {
    if (__priceMap) return __priceMap;
    var script = document.querySelector('script[data-nai-top-prices]');
    if (!script) { __priceMap = {}; return __priceMap; }
    try {
      __priceMap = JSON.parse(script.textContent.trim());
    } catch (e) {
      log('failed to parse price map', e);
      __priceMap = {};
    }
    return __priceMap;
  }

  // Format a price as "US$2,051" / "CA$2,765" — matches the
  // card-product Liquid filter chain (round, comma-thousands, no cents).
  function formatPrice(amount, prefix) {
    var n = parseFloat(amount);
    if (!n || n <= 0) return '';
    n = Math.round(n);
    return prefix + n.toLocaleString('en-US');
  }

  // Rewrite the price block on a cloned card to use the
  // per-product starting_price_usd / _cad metafields. We render the
  // SAME data-market-aware-price contract as snippets/card-product.liquid
  // so the global market-swap IIFE there picks up the new badges
  // automatically when the visitor flips USD ↔ CAD.
  function rewriteCardPrice(card, product) {
    var prices = getPriceMap();
    var record = prices[product.handle];
    if (!record) return; // Product not in seating collection — skip silently.

    var usd = parseFloat(record.usd);
    var cad = parseFloat(record.cad);
    var hasUsd = usd > 0;
    var hasCad = cad > 0;
    if (!hasUsd && !hasCad) return;

    var usdDisplay = formatPrice(usd, 'US$');
    var cadDisplay = formatPrice(cad, 'CA$');

    // Find the existing price node from the cloned template.
    var priceEl = card.querySelector('[data-market-aware-price], .product-card__starting-price');
    if (!priceEl) return; // Template card has no price block — bail.

    priceEl.setAttribute('data-market-aware-price', '');
    priceEl.setAttribute('data-has-usd', hasUsd ? 'true' : 'false');
    priceEl.setAttribute('data-has-cad', hasCad ? 'true' : 'false');

    var labelEl = priceEl.querySelector('[data-market-label], .product-card__starting-price-label');
    var valueEl = priceEl.querySelector('[data-market-value], .product-card__starting-price-value');

    if (labelEl) {
      labelEl.setAttribute('data-market-label', '');
      labelEl.textContent = 'List Price Starting From';
    }
    if (valueEl) {
      valueEl.setAttribute('data-market-value', '');
      valueEl.setAttribute('data-usd', usdDisplay);
      valueEl.setAttribute('data-cad', cadDisplay);
      // Read the visitor's current market and default to the matching
      // currency so the card renders correctly even before the global
      // market-swap observer in card-product.liquid runs.
      var mkt = 'USD';
      try { mkt = localStorage.getItem('nightingale_market') || 'USD'; } catch (e) {}
      var preferCad = mkt === 'CAD' && cadDisplay;
      valueEl.textContent = preferCad ? cadDisplay : (usdDisplay || cadDisplay);
    }
    if (labelEl) {
      var mkt2 = 'USD';
      try { mkt2 = localStorage.getItem('nightingale_market') || 'USD'; } catch (e) {}
      var preferCadLabel = mkt2 === 'CAD' && cadDisplay;
      labelEl.textContent = preferCadLabel
        ? 'CAD List Price Starting From'
        : 'USD List Price Starting From';
    }
  }

  function rewriteCardTextContent(card, product) {
    // Title — usually a heading or a specific class.
    var title = card.querySelector(
      '.card-product__title, .product-card__title, .card__heading, h3, h4, h2'
    );
    if (title) {
      var a = title.querySelector('a');
      if (a) {
        a.setAttribute('href', product.url);
        // The theme wraps the visible text in <span class="reversed-link__text">.
        // CSS uses :has(.reversed-link__text) to put the underline on the SPAN
        // (tight to the text) rather than on the full-width anchor. If we
        // overwrite the anchor's textContent we destroy that span and the
        // underline stretches across the whole card. So target the span
        // directly when present.
        var textSpan = a.querySelector('.reversed-link__text');
        if (textSpan) {
          textSpan.textContent = product.title;
        } else {
          swapTextNodeOrFirstTextChild(a, product.title);
        }
      } else {
        swapTextNodeOrFirstTextChild(title, product.title);
      }
    }
    // Strip any vendor element entirely — we don't want the vendor label
    // showing on auto-populated cards.
    var vendors = card.querySelectorAll(
      '.card-product__vendor, .product-card__vendor, .card__vendor, .vendor'
    );
    vendors.forEach(function (v) { v.remove(); });
    // Type (productType) — e.g. "MODERN TASK SEATING / EXECUTIVE SEATING".
    // If we have one, swap the link text + href. If we don't, strip the
    // element entirely so the template's "LOUNGE SEATING" doesn't leak
    // through to the wrong card.
    var type = card.querySelector(
      '.card-product__type, .product-card__type, .product-type'
    );
    if (type) {
      if (product.productType) {
        var typeLink = type.querySelector('a');
        if (typeLink) {
          typeLink.setAttribute('href', '/collections/types?q=' + encodeURIComponent(product.productType));
          // Same span preservation as title — keep .reversed-link__text intact
          // so the underline stays scoped to the text width.
          var typeTextSpan = typeLink.querySelector('.reversed-link__text');
          if (typeTextSpan) {
            typeTextSpan.textContent = product.productType;
          } else {
            swapTextNodeOrFirstTextChild(typeLink, product.productType);
          }
        } else {
          swapTextNodeOrFirstTextChild(type, product.productType);
        }
      } else {
        type.remove();
      }
    }
  }

  function escapeAttr(s) {
    return String(s).replace(/[<>&"']/g, function (c) {
      return ({ '<':'&lt;','>':'&gt;','&':'&amp;','"':'&quot;',"'":'&#39;' })[c];
    });
  }

  function buildFallbackCard(product) {
    // Minimal hand-rolled card if we can't find a curated card to clone.
    // Uses the theme's actual class names (.product-card, .product-card__*)
    // so existing CSS picks it up. Intentionally omits vendor.
    var div = document.createElement('div');
    div.className = 'product-card';
    div.innerHTML =
      '<div class="product-card__wrapper h-full">' +
        '<div class="product-card__image-wrapper">' +
          '<a href="' + escapeAttr(product.url) + '" aria-label="' + escapeAttr(product.title) + '">' +
            (product.image
              ? '<div class="media-wrapper product-card__image product-card__image--main"><img src="' +
                escapeAttr(product.image) + '" alt="' + escapeAttr(product.title) + '" loading="lazy" /></div>'
              : '') +
          '</a>' +
        '</div>' +
        '<div class="product-card__info">' +
          (product.productType
            ? '<p class="product-card__type text-sm-extra m-0"><a class="reversed-link" href="/collections/types?q=' +
              encodeURIComponent(product.productType) + '">' + escapeAttr(product.productType) + '</a></p>'
            : '') +
          '<h3 class="product-card__title text-pcard-title"><a class="reversed-link block" href="' +
            escapeAttr(product.url) + '"><span class="reversed-link__text">' +
            escapeAttr(product.title) + '</span></a></h3>' +
        '</div>' +
      '</div>';
    return div;
  }

  function renderProducts(gridEl, products) {
    if (!gridEl || !products.length) return;
    // Find a curated card to use as a template — anything inside the grid
    // that looks like a product card. We'll clone it for each result.
    // The theme's snippet uses `.product-card` (not `.card-product` — note
    // the hyphenation order); we match both for safety.
    var template = gridEl.querySelector(
      '.product-card, .card-product, [class*="product-card"], [class*="card-product"]'
    );
    log('found card template?', !!template);

    var out = document.createDocumentFragment();
    for (var i = 0; i < products.length && i < FINAL_PRODUCT_LIMIT; i++) {
      var product = products[i];
      var card;
      if (template) {
        card = template.cloneNode(true);
        rewriteCardImage(card, product.image);
        rewriteCardLinks(card, product.url);
        rewriteCardTextContent(card, product);
        // Rewrite the price using the server-rendered metafield map
        // so each card shows its own starting_price_usd / _cad value
        // instead of inheriting the template's frozen price.
        rewriteCardPrice(card, product);
      } else {
        card = buildFallbackCard(product);
      }
      out.appendChild(card);
    }
    gridEl.innerHTML = '';
    gridEl.appendChild(out);
    log('wrote', products.length, 'product cards into grid');

    // Belt-and-suspenders: explicitly invoke the global market swap so the
    // newly-inserted badges flip to CAD when that's the visitor's market.
    // The MutationObserver in card-product.liquid would catch this too, but
    // calling directly avoids any timing window where the user briefly sees
    // USD before the observer fires.
    if (typeof window.__naiApplyMarket === 'function') {
      window.__naiApplyMarket();
    }
  }

  var populated = false;
  function populate() {
    if (populated) return;
    var queryLists  = document.querySelectorAll('[data-nai-top-queries-list]');
    var productGrid = document.querySelector('[data-nai-top-products-grid]');
    log('populate() called — query lists:', queryLists.length, 'product grid:', !!productGrid);
    if (!queryLists.length && !productGrid) return;
    populated = true;
    getTop().then(function (top) {
      if (!top.queries.length && !top.products.length) {
        log('empty top-searches result — letting next focus retry');
        populated = false;
        return;
      }
      queryLists.forEach(function (l) { renderQueryList(l, top.queries); });
      if (productGrid && top.products.length) {
        renderProducts(productGrid, top.products);
      } else {
        log('skipped product render — productGrid:', !!productGrid, 'products:', top.products.length);
      }
    }).catch(function (err) {
      log('populate failed', err);
      populated = false;
    });
  }

  function tryPopulate() {
    var attempts = 0;
    var iv = setInterval(function () {
      attempts++;
      if (document.querySelector('[data-nai-top-queries-list]') ||
          document.querySelector('[data-nai-top-products-grid]')) {
        clearInterval(iv);
        populate();
      } else if (attempts > 25) {
        // 5s with no target — give up; targets aren't in initial DOM.
        clearInterval(iv);
        log('giving up — no target elements after 5s');
      }
    }, 200);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', tryPopulate);
  } else {
    tryPopulate();
  }

  // Belt-and-suspenders: re-attempt on first search-input focus or on
  // any search-toggle click — in case populate() ran before targets
  // were mounted or the fetch reset the populated flag.
  document.addEventListener('focusin', function (e) {
    var t = e.target;
    if (t && t.matches && (t.matches('.search__input') || t.matches('input[name="q"]'))) {
      populate();
    }
  }, true);
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    if (t.closest('.search__field-icon, .predictive-search-icon, [data-search-toggle]')) {
      populate();
    }
  });
})();
